import React from 'react';
import cls from 'classnames';
import { useSelector } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { CSSTransitionClassNames } from 'react-transition-group/CSSTransition';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { groupsTabClicked } from '@wix/bi-logger-groups/v2';
import {
  AvatarSize,
  Text,
  TextButtonPriority,
  TextTypography,
} from 'wix-ui-tpa';

import { GroupAppKey } from 'store/groups/types';
import { Role as GroupRole } from '@wix/ambassador-social-groups-v2-group-role/types';
import { selectMembers, selectMembersMetadata } from 'store/members/selectors';
import {
  selectGroupSlug,
  selectIsAppAvailable,
  selectIsJoinedGroupMember,
  selectMemberLabel,
  selectMembersCount,
} from 'store/groups/selectors';

import { UISref } from 'common/router';
import { Box } from 'common/components/Box/Box';
import { useDidUpdate } from 'common/hooks';
import { UserCard } from 'common/components/Cards/UserCard';
import { CustomTextButton } from 'common/components/CustomTextButton';
import { useController } from 'common/context/controller';

import {
  FollowButton,
  AdminBadge,
} from 'Group/Widget/MembersPage/GroupMembers/Member';

import * as discussionStyles from '../DiscussionPage.st.css';
import { classes } from './GroupMembers.st.css';
import { MEMBER_LABELS } from 'store/groups/constants';

interface IGroupMembersProps {
  groupId: string;
}

export function GroupMembers(props: IGroupMembersProps) {
  const { groupId } = props;
  const bi = useBi();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const { members$ } = useController();
  const meta = useSelector(selectMembersMetadata);
  const members = useSelector(selectMembers).slice(0, 5);
  const slug = useSelector(selectGroupSlug(groupId));
  const isJoined = useSelector(selectIsJoinedGroupMember(groupId));
  const membersLabel = useSelector(selectMemberLabel(groupId));
  const membersCount = useSelector(selectMembersCount(groupId));
  const isAvailable = useSelector(
    selectIsAppAvailable({
      groupId,
      application: GroupAppKey.MEMBERS_APP,
    }),
  );

  useDidUpdate(() => {
    if (isAvailable) {
      members$.fetch(groupId, 5);
    }
  }, [isAvailable, isJoined]);

  if (!isAvailable) {
    return null;
  }

  return (
    <Box article withSideBorders={!isMobile} data-hook="members-widget">
      <Text
        className={cls(discussionStyles.classes.widgetHeader, classes.header)}
      >
        {t(membersLabel.label)}
      </Text>
      {(() => {
        if (meta.loading) {
          return <GroupMembersSkeleton />;
        }

        if (!members.length) {
          return (
            <Text
              tagName="p"
              typography={TextTypography.runningText}
              className={cls(classes.description, classes.empty)}
            >
              {t('groups-web.discussion.members.empty')}
            </Text>
          );
        }

        return (
          <ul className={classes.list}>
            <TransitionGroup>
              {members.map((member) => (
                <CSSTransition
                  timeout={200}
                  classNames={classes as CSSTransitionClassNames}
                  key={member.memberId}
                >
                  <li className={classes.member}>
                    <UserCard
                      profile={member}
                      showBadges={false}
                      className={classes.profile}
                      avatarSize={AvatarSize.medium}
                      subtitle={
                        member.role === GroupRole.ADMIN ? <AdminBadge /> : null
                      }
                      action={
                        <div className={classes.actions}>
                          <FollowButton
                            useTextButton
                            groupId={groupId}
                            isPrivate={member.isPrivate}
                            memberId={member.siteMemberId as string}
                          />
                        </div>
                      }
                    />
                  </li>
                </CSSTransition>
              ))}
            </TransitionGroup>
          </ul>
        );
      })()}
      <div className={classes.ctaButtonWrapper}>
        <UISref state="group.members" params={{ slug }}>
          <CustomTextButton
            as="a"
            onClick={handleOpenMembersClick}
            priority={TextButtonPriority.primary}
          >
            {t(
              `${
                membersLabel.isCustom
                  ? MEMBER_LABELS.Custom
                  : membersLabel.label
              }.discussion.members.see-all`,
              {
                membersLabel: t(membersLabel.label),
                total: membersCount,
              },
            )}
          </CustomTextButton>
        </UISref>
      </div>
    </Box>
  );

  function handleOpenMembersClick() {
    bi.report(
      groupsTabClicked({
        group_id: groupId,
        origin: 'see_all_members_link',
        name: 'members',
      }),
    );
  }
}

GroupMembers.displayName = 'GroupMembers';

function GroupMembersSkeleton() {
  return (
    <ul className={classes.list}>
      <li className={classes.member}>
        <UserCard loading avatarSize={AvatarSize.medium} />
      </li>
      <li className={classes.member}>
        <UserCard loading avatarSize={AvatarSize.medium} />
      </li>
      <li className={classes.member}>
        <UserCard loading avatarSize={AvatarSize.medium} />
      </li>
    </ul>
  );
}
